/**
 * Content-based classifier for the QuickBooks exports that feed the multi-file
 * "rebuild from QuickBooks" bundle importer. Given a filename and the head of a
 * file's content, decide which importer it belongs to.
 *
 * The bundle accepts four real QB export shapes, all distinguishable by their
 * header row (and, for the two Balance-Detail reports, by the transaction Type
 * values, since AR and AP share an identical column layout):
 *
 *   - iif                      → QB lists export (chart/customers/vendors).
 *                                IIF is a tab-delimited keyword format whose
 *                                lines begin with `!`/keyword tokens (!HDR,
 *                                !ACCNT, !CUST, !VEND, !TRNS, ...).
 *   - journal_csv              → "Trans #,Type,Date,Num,Name,Memo,Account,
 *                                Debit,Credit" — the canonical ledger spine.
 *   - transactions_csv         → AR Customer Balance Detail
 *                                (",Type,Date,Num,…,Amount,Balance" with
 *                                Invoice/Payment/Credit Memo rows).
 *   - vendor_transactions_csv  → AP Vendor Balance Detail (same column shape,
 *                                Bill / Bill Pmt -Check rows).
 *
 * The classifier is pure and dependency-free so it can run both in the upload
 * UI (live per-file preview) and on the server (`/start` re-validates the
 * user's override against the file's actual content).
 */

export type QbFileType =
	| 'iif'
	| 'journal_csv'
	| 'transactions_csv'
	| 'vendor_transactions_csv'
	| 'unknown';

export interface QbClassification {
	type: QbFileType;
	confidence: 'high' | 'medium' | 'low';
	/** Human-readable explanation, surfaced in the UI next to the file. */
	reason: string;
}

/** Exact Journal CSV header (lower-cased, trimmed) — see import-journal-csv.ts. */
const JOURNAL_HEADER = ['trans #', 'type', 'date', 'num', 'name', 'memo', 'account', 'debit', 'credit'];

const AR_TYPES = new Set(['invoice', 'payment', 'credit memo']);
const AP_TYPES = new Set(['bill', 'bill pmt -check']);

/** Tolerant single-line CSV splitter (quoted fields w/ embedded commas). */
function splitCsvLine(line: string): string[] {
	const out: string[] = [];
	let cur = '';
	let inQuotes = false;
	for (let i = 0; i < line.length; i++) {
		const c = line[i];
		if (inQuotes) {
			if (c === '"') {
				if (line[i + 1] === '"') { cur += '"'; i++; }
				else { inQuotes = false; }
			} else {
				cur += c;
			}
		} else if (c === '"') {
			inQuotes = true;
		} else if (c === ',') {
			out.push(cur);
			cur = '';
		} else {
			cur += c;
		}
	}
	out.push(cur);
	return out;
}

function looksLikeIif(filename: string, lines: string[]): boolean {
	if (/\.iif$/i.test(filename.trim())) return true;
	// IIF keyword lines start with `!` (definition) or a bare keyword token
	// followed by a TAB. The chart/list keywords are the reliable markers.
	for (const raw of lines) {
		const line = raw.trim();
		if (!line) continue;
		if (/^!(HDR|ACCNT|CUST|VEND|TRNS|SPL|CLASS|INVITEM|TERMS)\b/i.test(line)) return true;
		// Some exports omit the `!` on the very first line; a leading keyword +
		// TAB is still a strong IIF signal and never matches a CSV header.
		if (/^(ACCNT|CUST|VEND|TRNS)\t/i.test(line)) return true;
		break; // only inspect the first non-blank line beyond the `!` scan
	}
	return false;
}

/**
 * Classify a single uploaded QuickBooks file. `head` need only contain the
 * first chunk of the file (a few KB / dozens of lines is plenty).
 */
export function classifyQbFile(filename: string, head: string): QbClassification {
	const lines = head.split(/\r?\n/);

	if (looksLikeIif(filename, lines)) {
		return { type: 'iif', confidence: 'high', reason: 'IIF keyword markers (!ACCNT/!CUST/…) or .iif extension' };
	}

	// Find the first CSV header row and, for the balance-detail shape, sample
	// the transaction Type values that follow it.
	let sawBalanceDetailHeader = false;
	// "Type" sits at col 1 in the compact/detailed layouts, col 2 in the FULL
	// layout (which leads with a "Trans #" column).
	let typeIdx = 1;
	let arHits = 0;
	let apHits = 0;
	for (const raw of lines) {
		if (!raw.trim()) continue;
		const cells = splitCsvLine(raw).map((c) => c.trim().toLowerCase());

		// Journal CSV: header begins with "Trans #". Match the leading columns
		// (QB sometimes appends trailing blank columns).
		if (cells[0] === 'trans #') {
			const headerOk = JOURNAL_HEADER.every((h, idx) => cells[idx] === h);
			return headerOk
				? { type: 'journal_csv', confidence: 'high', reason: 'Journal header: Trans #,Type,Date,…,Debit,Credit' }
				: { type: 'journal_csv', confidence: 'medium', reason: 'Leading "Trans #" column (Journal-style export)' };
		}

		// Balance-Detail header: empty first cell, then "Type" — either directly
		// (compact/detailed) or behind a "Trans #" column (full layout).
		if (cells[0] === '' && (cells[1] === 'type' || (cells[1] === 'trans #' && cells[2] === 'type'))) {
			sawBalanceDetailHeader = true;
			typeIdx = cells[1] === 'type' ? 1 : 2;
			continue;
		}

		// Data rows after a balance-detail header carry the discriminating Type.
		const type = cells[typeIdx];
		if (type) {
			if (AP_TYPES.has(type)) apHits++;
			else if (AR_TYPES.has(type)) arHits++;
		}
	}

	// AP markers win when present — "Bill"/"Bill Pmt -Check" never appear in an
	// AR Customer Balance Detail, so any AP hit is decisive.
	if (apHits > 0) {
		return { type: 'vendor_transactions_csv', confidence: 'high', reason: `Vendor rows (Bill / Bill Pmt -Check ×${apHits})` };
	}
	if (arHits > 0) {
		return { type: 'transactions_csv', confidence: 'high', reason: `Customer rows (Invoice / Payment / Credit Memo ×${arHits})` };
	}
	if (sawBalanceDetailHeader) {
		// Header matched but no recognizable data rows in the sampled head —
		// likely a Balance Detail report; can't tell AR from AP yet.
		return { type: 'transactions_csv', confidence: 'low', reason: 'Balance-Detail header but no Type rows sampled — defaulting to AR; please confirm' };
	}

	return { type: 'unknown', confidence: 'low', reason: 'No recognized QuickBooks header or markers' };
}
